import PatientMassage from '../assets/images/patient-massage.png'
import PatientStandingArmStretch from '../assets/images/patient-standing-arm-stretch.png'
import PatientGenericInjury from '../assets/images/patient-generic-injury.png'
import PatientGenericPain from '../assets/images/patient-generic-pain.png'
import PatientGenericPregnant from '../assets/images/patient-generic-pregnant.png'
import PatientGenericBaby from '../assets/images/patient-generic-baby.png'

const techniques = [
  'Soft tissue massage',
  'Stretching',
  'Gentle manipulation',
  'Joint articulation',
  'Muscle energy technique',
  'Cranial',
  'Visceral',
  'Fascial unwinding',
]

const servicesData = [
  {
    title: 'INJURIES & ACCIDENTS',
    image: PatientGenericInjury,
    description: [
      'We aid injury recovery through osteopathy by promoting natural healing. Using gentle manipulation of muscles, joints, and tissues, we improve blood flow, reduce inflammation, and enhance range of motion.',
      'By addressing imbalances and restoring alignment, we alleviate pain and prevent future injuries. Our personalised treatment plans consider the whole body, addressing all contributing factors for a faster and more complete recovery.',
    ],
    effectiveFor: ['Sports Injuries', 'Sprains', 'Strains', 'Repetitive stress injury'],
  },
  {
    title: 'CHRONIC PAIN',
    image: PatientGenericPain,
    description: [
      'We recognise that chronic pain can become overwhelming, therefore we manage chronic pain holistically, non-invasively and alongside a multi-disciplinary team as required. As osteopaths we focus on the musculoskeletal system, while also incorporating elements of breathing, identifying obstacles to recovery and setting goals to achieve optimal pain management.',
      'We identify and address the root causes of pain, promoting long-term relief and improved mobility. Our osteopathic treatments support natural healing, helping patients achieve a better quality of life with less pain and discomfort.',
    ],
    effectiveFor: ['Arthritis', 'Fibromyalgia', 'Back pain', 'Migraines'],
  },
  {
    title: 'PRE- & POST PARTUM',
    image: PatientGenericPregnant,
    description: [
      'During pregnancy, we enhance alignment and body function, boosting circulation and reducing swelling to increase overall comfort. We are here to support your body throughout this constantly evolving process.',
      'Postpartum, we help optimise the recover from childbirth strains, restoring balance and mobility. We support natural healing, helping mothers regain strength and reduce pain. We also offer postural advice for breastfeeding, movement, and a smooth return to exercise or work, easing the transition into motherhood.',
    ],
    effectiveFor: ['Back pain', 'Pelvic discomfort', 'Easier delivery', 'Sciatica', 'Coccyx'],
  },
  {
    title: 'NEWBORN, INFANTS & KIDS',
    image: PatientGenericBaby,
    description: [
      'During pregnancy, birth, and their subsequent growth, babies experience various strains, torsions, and growth spurts. We are dedicated to restoring alignment and easing these tensions to enhance feeding, digestion, movement, and overall development.',
      'For children, we can assist with growing pains, injuries, postural development, balance and co-ordination.',
    ],
    effectiveFor: [
      'Colic',
      'Reflux',
      'Feeding difficulties',
      'Motor skill development',
      'Growing pains',
      'Postural problems',
    ],
  },
]

const Services = () => {
  return (
    <>
      <section className='container'>
        <IntroSection />
      </section>
      <section className='container'>
        <TechniquesSection />
      </section>
      <ServicesCardsSection />
      <section className='container'>
        <AppointmentInfoSection />
      </section>
    </>
  )
}

const IntroSection = () => (
  <div className='mb-8 lg:mb-20 lg:max-w-[65%] lg:pr-[72.5px]'>
    <h1 className='mb-10 font-serif text-5xl font-bold text-blue-dark'>What we help with</h1>
    <h2 className='mb-3 text-lg font-bold leading-none text-blue-dark'>WHAT IS AN OSTEOPATH</h2>
    <p className='mb-4'>
      Osteopathy is a form of manual medicine that uses hands-on techniques to address the structure and
      function of the body to promote healing.
    </p>
    <h2 className='mb-3 text-lg font-bold leading-none text-blue-dark'>WHAT WE OFFER</h2>
    <p className='mb-4'>
      We offer a comprehensive range of osteopathic treatments tailored to all ages and unique health needs.
      Our goal is to encourage functionality while reducing stiffness, pain, and discomfort. We also provide
      exercise and lifestyle advice to assist recovery and optimise results.
    </p>
    <p>
      Our aim is to deliver effective, friendly care for the whole family, addressing the root cause of
      symptoms and promoting overall well-being. If a condition falls outside our scope of practice, we may
      refer you for additional imaging, second opinions, or specialized care.
    </p>
  </div>
)

const TechniquesSection = () => (
  <div className='mb-8 grid items-center gap-12 lg:mb-20 lg:grid-cols-2'>
    <div className='lg:pr-[72.5px]'>
      <img
        src={PatientMassage}
        className='mx-auto block h-auto max-w-full'
        alt='Patient Massage'
        width='700'
        height='500'
      />
    </div>
    <div className='lg:pl-[72.5px]'>
      <h2 className='mb-3 text-lg font-bold leading-none text-blue-dark'>TECHNIQUES WE USE</h2>
      <ul className='mb-4 list-disc pl-8 text-blue-dark'>
        {techniques.map((technique) => (
          <li key={technique}>{technique}</li>
        ))}
      </ul>
      <p className='text-blue-dark'>
        We also commonly give advice on exercise and lifestyle to assist recovery or optimize results.
      </p>
    </div>
  </div>
)

const ServicesCardsSection = () => (
  <section className='bg-brand-gradient-reverse p-4 lg:container lg:bg-none lg:p-0'>
    <div className='lg:rounded-lg lg:bg-brand-gradient-reverse lg:p-8'>
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        {servicesData.map((service) => (
          <ServiceCard key={service.title} {...service} />
        ))}
      </div>
    </div>
  </section>
)

const ServiceCard = ({ title, image, description, effectiveFor }) => (
  <div className='h-full rounded border-2 border-teal-border/20 bg-white/10 text-white'>
    <div className='p-3'>
      <img src={image} className='block h-auto w-full rounded-t' alt={title} />
    </div>
    <div className='p-5'>
      <h2 className='mb-2 text-lg font-semibold'>{title}</h2>
      {description.map((para) => (
        <p key={para} className='mb-3'>
          {para}
        </p>
      ))}
      <div className='mb-3'>
        <p>
          <strong>This is effective for:</strong>
        </p>
      </div>
      <div className='flex flex-wrap gap-2'>
        {effectiveFor.map((tag) => (
          <div key={tag} className='rounded-[32px] border border-teal-border/20 bg-white/10 px-4 py-2'>
            {tag}
          </div>
        ))}
      </div>
    </div>
  </div>
)

const AppointmentInfoSection = () => (
  <div className='grid items-center gap-12 py-12 lg:grid-cols-2'>
    <div className='lg:pr-14'>
      <img
        src={PatientStandingArmStretch}
        className='mx-auto block h-auto max-w-full'
        alt='Patient Standing Arm Stretch'
        width='700'
        height='500'
      />
    </div>
    <div className='lg:pl-14'>
      <InfoSection
        title='APPOINTMENT LENGTH'
        content='Please allow up to 45 minutes for the first consultation and up to 40 minutes for subsequent consultations.'
      />
      <InfoSection
        title='WHAT TO WEAR'
        content='Comfort is key, so we recommend wearing loose, comfortable clothing like a T-shirt or shorts. For an accurate diagnosis and treatment, your osteopath may ask you to remove certain clothing down to your underwear. A regular bra with a clip is preferred, as a sports bra may cover more of the back and spine, making treatment more difficult. Towels will be provided for draping. If you are uncomfortable or unable to remove clothing, please inform your osteopath.'
      />
      <InfoSection
        title='ACC'
        content='We are ACC registered and can open an ACC claim for your injury if needed. If you already have an ACC claim, please bring the number to your consultation. Additionally, please bring any medical notes, referral letters, test results, or imaging related to your injury to help us better understand your condition.'
      />
    </div>
  </div>
)

const InfoSection = ({ title, content }) => (
  <>
    <h2 className='mb-3 text-lg font-bold leading-none text-blue-dark'>{title}</h2>
    <p className='mb-4 text-blue-dark'>{content}</p>
  </>
)

export default Services
